import { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Card, Form, Spinner, Table } from 'react-bootstrap'
import axios from 'axios'
import { toast } from 'react-toastify'

import { URL as BACKEND_URL, fetchNewToken } from '../../gql/client'
import { STORAGE_NAMES } from '../../helpers/consts'
import { useTelephony } from '../../hooks/useTelephony'
import { TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT } from '../../types/telephony'

const CALL_LOG_LIMIT = 50

// Local 'YYYY-MM-DD' for <input type="date"> defaults (defaults to today, like the
// supervisor Call log).
const todayStr = () => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const formatSeconds = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (!mins) return `${secs}s`
  return `${mins}m ${secs}s`
}

const CALL_LOG_RESULTS = ['SALE', 'NCSE', 'INST', 'CALLBK', 'NINT', 'NANS', 'DROP', 'QUEUE', 'PDROP', 'DNC', 'DEC', 'AA']

interface CallLogEntry {
  start_time: string
  agent: string
  campaign: string
  campaign_name?: string
  direction?: string
  phone: string
  source_phone?: string
  duration: number
  status: string
  term_reason: string
  uniqueid: string
  recording_id: string
  recording_url: string
  first_name?: string
  last_name?: string
  lead_comments?: string
  // Missed-call rows ("No atendidas" filter)
  missed?: boolean
  communication_log_id?: number
  handled_by_name?: string
}

interface CallLogResponse {
  calls: CallLogEntry[]
  total: number
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const authGet = async <T,>(url: string): Promise<T> => {
  try {
    const res = await axios.get<T>(url, { headers: authHeaders() })
    return res.data
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        const res = await axios.get<T>(url, { headers: { Authorization: `Bearer ${newToken}` } })
        return res.data
      }
    }
    throw err
  }
}

export const MyCallsPage = () => {
  const telephony = useTelephony()
  const enabled = Boolean(telephony.config?.vicidial_enabled)

  const [calls, setCalls] = useState<CallLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dateFrom, setDateFrom] = useState(todayStr())
  const [dateTo, setDateTo] = useState(todayStr())
  const [phone, setPhone] = useState('')
  const [status, setStatus] = useState('')
  const [missed, setMissed] = useState(false)

  const loadCalls = useCallback(
    async (nextOffset = 0, missedOverride?: boolean) => {
      if (!enabled) return
      const useMissed = missedOverride ?? missed
      setLoading(true)
      try {
        const params = new URLSearchParams()
        if (dateFrom) params.set('date_from', dateFrom)
        if (dateTo) params.set('date_to', dateTo)
        if (phone) params.set('phone', phone)
        if (useMissed) params.set('missed', '1')
        else if (status) params.set('status', status)
        params.set('limit', String(CALL_LOG_LIMIT))
        params.set('offset', String(nextOffset))
        const data = await authGet<CallLogResponse>(
          `${BACKEND_URL}/api/telephony/my/calls?${params.toString()}`
        )
        setCalls(data.calls || [])
        setTotal(data.total || 0)
        setOffset(nextOffset)
        setError('')
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load your calls')
      } finally {
        setLoading(false)
      }
    },
    [enabled, dateFrom, dateTo, phone, status, missed]
  )

  // Missed call: claim FIRST (records who returns it + dismisses teammates' copies),
  // then dial. If someone already returned it, warn and don't dial.
  const handleMissedCallBack = async (call: CallLogEntry) => {
    const n = String(call.phone || '').replace(/[^0-9+]/g, '')
    if (!n) return
    if (call.communication_log_id != null) {
      try {
        const res = await axios.post(
          `${BACKEND_URL}/api/telephony/missed-calls/claim`,
          { communication_log_id: call.communication_log_id },
          { headers: authHeaders() }
        )
        if (res.data?.already) {
          toast.info(`Already returned by ${res.data?.handled_by_name || '—'}`)
          loadCalls(offset)
          return
        }
      } catch {
        // fall through and let them dial anyway
      }
    }
    window.dispatchEvent(
      new CustomEvent(TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT, { detail: { phone: n, autoDial: true } })
    )
    loadCalls(offset)
  }

  useEffect(() => {
    if (enabled) loadCalls(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  if (!enabled) {
    return (
      <Card className='shadow-sm'>
        <Card.Body className='text-center text-muted py-5'>
          <i className='bi bi-telephone-x fs-1 d-block mb-3' />
          PolicyDial is not enabled for your user. Ask your administrator to enable it from Users →
          PolicyDial Settings.
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card className='shadow-sm'>
      <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
        <div>
          <div className='fw-semibold'>My calls</div>
          <div className='text-muted small'>Your PolicyDial calls and recordings.</div>
        </div>
      </Card.Header>
      <Card.Body>
        <div className='d-flex align-items-end gap-2 flex-wrap mb-3'>
          <Form.Group>
            <Form.Label className='fw-semibold text-gray-700 fs-8 mb-1'>From</Form.Label>
            <Form.Control
              size='sm'
              type='date'
              style={{ width: 150 }}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className='fw-semibold text-gray-700 fs-8 mb-1'>To</Form.Label>
            <Form.Control
              size='sm'
              type='date'
              style={{ width: 150 }}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className='fw-semibold text-gray-700 fs-8 mb-1'>Phone</Form.Label>
            <Form.Control
              size='sm'
              style={{ width: 160 }}
              placeholder='Customer number'
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className='fw-semibold text-gray-700 fs-8 mb-1 d-block'>View</Form.Label>
            <ButtonGroup size='sm'>
              <Button
                variant={missed ? 'light' : 'primary'}
                onClick={() => { setMissed(false); loadCalls(0, false) }}
                disabled={loading}
              >
                All
              </Button>
              <Button
                variant={missed ? 'danger' : 'light'}
                onClick={() => { setMissed(true); loadCalls(0, true) }}
                disabled={loading}
              >
                Missed
              </Button>
            </ButtonGroup>
          </Form.Group>
          {!missed && (
            <Form.Group>
              <Form.Label className='fw-semibold text-gray-700 fs-8 mb-1'>Result</Form.Label>
              <Form.Select
                size='sm'
                style={{ width: 140 }}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value=''>All</option>
                {CALL_LOG_RESULTS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Button size='sm' variant='primary' onClick={() => loadCalls(0)} disabled={loading}>
            Apply
          </Button>
          <Button
            size='sm'
            variant='light'
            onClick={() => {
              setPhone('')
              setStatus('')
              setMissed(false)
              setDateFrom(todayStr())
              setDateTo(todayStr())
              loadCalls(0, false)
            }}
            disabled={loading}
          >
            Clear
          </Button>
          {loading && <Spinner animation='border' size='sm' />}
        </div>

        {error && <Alert variant='danger'>{error}</Alert>}
        <Table size='sm' responsive hover className='align-middle'>
          <thead className='table-light'>
            <tr>
              <th>Date / time</th>
              <th>Campaign</th>
              <th>Direction</th>
              <th>Source phone</th>
              <th>Phone destination</th>
              <th>Customer</th>
              <th className='text-end'>Duration</th>
              <th>Result</th>
              <th style={{ minWidth: 240 }}>Recording</th>
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className='text-center text-muted py-4'>
                  No calls for the selected filters
                </td>
              </tr>
            )}
            {calls.map((call) => (
              <tr key={call.uniqueid || `${call.start_time}-${call.recording_id}`}>
                <td className='font-monospace small'>{call.start_time}</td>
                <td>{call.campaign_name || call.campaign || '—'}</td>
                <td>
                  {call.direction ? (
                    <Badge bg={call.direction === 'INBOUND' ? 'info' : 'secondary'} className='border-0'>
                      {call.direction}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{call.source_phone || '—'}</td>
                <td>{call.phone || '—'}</td>
                <td>{[call.first_name, call.last_name].filter(Boolean).join(' ').trim() || '—'}</td>
                <td className='text-end'>{call.duration < 0 ? '—' : formatSeconds(call.duration)}</td>
                <td>
                  {call.missed ? (
                    <Badge bg='danger' className='border-0'>
                      Missed
                    </Badge>
                  ) : call.term_reason === 'AGENT' ? (
                    <Badge
                      bg={
                        ['SALE', 'NCSE', 'INST'].includes(call.status)
                          ? 'success'
                          : call.status === 'NODISP'
                          ? 'secondary'
                          : ['DNC', 'DROP', 'PDROP'].includes(call.status)
                          ? 'warning'
                          : 'light'
                      }
                      text={
                        ['SALE', 'NCSE', 'INST'].includes(call.status) || call.status === 'NODISP'
                          ? undefined
                          : 'dark'
                      }
                      className='border'
                    >
                      {call.status || 'Dispositioned'}
                    </Badge>
                  ) : (
                    <>
                      <Badge bg='success' className='border-0'>
                        Handled
                      </Badge>
                      {call.status && <span className='text-muted small ms-1'>({call.status})</span>}
                    </>
                  )}
                </td>
                <td>
                  {call.missed ? (
                    call.handled_by_name ? (
                      <span className='text-muted small'>
                        <i className='bi bi-check2-circle text-success me-1' />
                        Returned by {call.handled_by_name}
                      </span>
                    ) : (
                      <Button
                        size='sm'
                        variant='light-danger'
                        onClick={() => handleMissedCallBack(call)}
                      >
                        <i className='bi bi-telephone-outbound me-1' />
                        Call back
                      </Button>
                    )
                  ) : call.recording_url ? (
                    <audio controls preload='none' src={call.recording_url} style={{ height: 32, maxWidth: 240 }} />
                  ) : (
                    <span className='text-muted small'>No recording</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {total > 0 && (
          <div className='d-flex justify-content-between align-items-center mt-2'>
            <div className='text-muted small'>
              {offset + 1}–{Math.min(offset + CALL_LOG_LIMIT, total)} of {total}
            </div>
            <ButtonGroup size='sm'>
              <Button
                variant='light'
                disabled={loading || offset === 0}
                onClick={() => loadCalls(Math.max(0, offset - CALL_LOG_LIMIT))}
              >
                ← Prev
              </Button>
              <Button
                variant='light'
                disabled={loading || offset + CALL_LOG_LIMIT >= total}
                onClick={() => loadCalls(offset + CALL_LOG_LIMIT)}
              >
                Next →
              </Button>
            </ButtonGroup>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
